import {fetchJSON, contentEndpoint} from './remote-data';
import {solarState, solarPosition, weatherSnapshot, currentWeather} from './weather-data';
import {savedDay} from './api-data';
import {currentLocale, translate} from './preferences';

export default function localWidget(root = document, signal) {
    const widget = root.querySelector('[data-local-widget]');
    if (!widget) return;
    const clock = widget.querySelector('[data-clock]');
    const label = widget.querySelector('[data-weather-label]');
    const location = widget.querySelector('[data-location]');
    const temperature = widget.querySelector('[data-temperature]');
    const condition = widget.querySelector('[data-condition]');
    const sky = widget.querySelector('[data-sky-body]');
    const countdown = widget.querySelector('[data-sun-countdown]');
    const sun = widget.querySelector('[data-sun]');
    const moon = widget.querySelector('[data-moon]');
    const time = new Intl.DateTimeFormat('en-GB', {timeZone: 'Asia/Taipei', hour: '2-digit', minute: '2-digit', second: '2-digit', hourCycle: 'h23'});
    const refreshInterval = 600000;
    let snapshot, pending = false, lastAttempt = null;
    // Retire the old direct-provider cache. New responses stay only in page memory.
    try { sessionStorage.removeItem('tiwb-weather-taipei-v2'); } catch (_) {}

    function tick() {
        const now = Date.now(), locale = currentLocale();
        widget.lang = locale;
        widget.setAttribute('aria-label', translate('weatherWidget'));
        clock.textContent = time.format(now); clock.dateTime = new Date(now).toISOString();
        const copy = snapshot?.translations[locale];
        const place = copy?.location || (locale === 'zh-TW' ? '臺北' : 'taipei');
        location.textContent = translate('weatherTimezone').replace('{location}', locale === 'en-US' ? place.toLowerCase() : place);
        const current = currentWeather(snapshot, now);
        const stale = !!current && now - current.dataTime > 1800000;
        label.hidden = !current;
        temperature.textContent = current ? `${Math.round(current.temperature)}°C` : '';
        condition.textContent = current ? copy?.condition || snapshot.translations['en-US'].condition || translate('weatherUnknown') : '';
        condition.hidden = !condition.textContent;
        const state = solarState(snapshot?.sun?.days, now);
        const complete = current && state && current.source === 'cwa' && snapshot.sun.source === 'cwa' &&
            current.code !== 'unknown' && snapshot.status !== 'degraded' &&
            snapshot.sun.days.some(day => day.date === savedDay(new Date(now + 86400000).toISOString()));
        widget.dataset.weatherStatus = stale ? 'stale' : !current && !state ? 'unavailable' : complete ? 'ready' : 'degraded';
        widget.dataset.solarPhase = !state ? 'unavailable' : state.day ? 'day' : 'night';
        countdown.textContent = state?.until != null ? translate(state.event === 'sunrise' ? 'weatherToSunrise' : 'weatherToSunset').replace('{hours}', Math.floor(state.until / 60)).replace('{minutes}', String(state.until % 60).padStart(2, '0')) : '';
        sky.hidden = !state; sun.hidden = !state?.day; moon.hidden = !state || state.day;
        if (state) {
            const position = solarPosition(state.progress);
            sky.dataset.progressKnown = String(state.progress !== null);
            sky.style.setProperty('--dayline-progress', `${position.left}%`);
            sky.style.top = `${position.top}px`;
        }
    }
    async function refreshWeather() {
        const now = Date.now();
        if (widget.dataset.weather !== 'true' || document.hidden || pending || (lastAttempt !== null && now >= lastAttempt && now - lastAttempt < refreshInterval)) return;
        lastAttempt = now; pending = true;
        try {
            const response = await fetchJSON(contentEndpoint(widget.dataset.api, '/api/v1/weather'), {cache: 'no-store', signal});
            if (signal?.aborted) return;
            snapshot = weatherSnapshot(response, snapshot);
        } catch (error) {
            // A disabled service clears its data. Temporary failures preserve only unexpired blocks.
            if (error.status === 404) snapshot = undefined;
        } finally { pending = false; if (!signal?.aborted) tick(); }
    }
    window.addEventListener('tiwb:language', tick, {signal});
    tick();
    const timer = setInterval(() => { if (!document.hidden) { tick(); refreshWeather(); } }, 1000);
    signal?.addEventListener('abort', () => clearInterval(timer), {once: true});
    document.addEventListener('visibilitychange', () => { if (!document.hidden) { tick(); refreshWeather(); } }, {signal});
    return refreshWeather();
}
